import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import { useThemeColors, useIsDark } from '@/shared/theme/useThemeColors';
import { typography } from '@/shared/theme/tokens';

// Project-standard select, built on MUI Autocomplete. One component for:
//   - single OR multi select (multiple prop, default single)
//   - type-to-search (searchable prop, default true) / autocomplete
//   - per-option leading icons
//   - sizes ('sm' | 'md')
//   - dark-theme aware + driven by the common MUI theme (popup auto-themes via
//     palette.mode; we add token-driven slotProps for the rest)
//
// Options: Array<{ value, label, icon?, group? }>.
// Value:
//   single → the selected `value` (primitive) | null
//   multi  → array of selected `value`s
// onChange receives the value(s), NOT the event:
//   single → (value | null)
//   multi  → (value[])

const POPPER_Z = 1402; // clear the FilterDrawer panel (1301)

function normalizeOptions(options) {
  return (options ?? []).map((o) =>
    typeof o === 'string' ? { value: o, label: o } : o,
  );
}

export function AppSelect({
  options,
  value,
  onChange,
  multiple = false,
  searchable = true,
  placeholder = 'Select…',
  label,
  size = 'md',
  disabled = false,
  clearable = true,
  ariaLabel,
  fullWidth = true,
  sx,
}) {
  const c = useThemeColors();
  const isDark = useIsDark();
  const opts = normalizeOptions(options);

  // Map between the stored primitive value(s) and the option objects MUI works with.
  const byValue = new Map(opts.map((o) => [o.value, o]));
  const selected = multiple
    ? (Array.isArray(value) ? value : []).map((v) => byValue.get(v) ?? { value: v, label: String(v) })
    : (value == null ? null : byValue.get(value) ?? { value, label: String(value) });

  const handleChange = (_e, next) => {
    if (multiple) onChange?.((next ?? []).map((o) => (o?.value ?? o)));
    else onChange?.(next ? (next.value ?? next) : null);
  };

  const fontSize = size === 'sm' ? 12 : 13;
  const fieldBg = c.bg;
  // Field height tuned to match the project's text inputs (and keep compact rows
  // from growing). sm ~32px, md ~38px.
  const fieldH = size === 'sm' ? 32 : 38;

  return (
    <Autocomplete
      multiple={multiple}
      options={opts}
      value={selected}
      onChange={handleChange}
      disabled={disabled}
      fullWidth={fullWidth}
      // Root is display:block by default — make it flex so the field fills the
      // wrapper height and stays vertically centered (e.g. inline in a table cell).
      sx={{ display: 'flex', alignItems: 'center', '& .MuiFormControl-root': { width: '100%', my: 0 } }}
      disableCloseOnSelect={multiple}
      disableClearable={!clearable}
      openOnFocus
      size="small"
      groupBy={opts.some((o) => o.group) ? (o) => o.group ?? '' : undefined}
      isOptionEqualToValue={(o, v) => o.value === (v?.value ?? v)}
      getOptionLabel={(o) => o?.label ?? ''}
      slotProps={{
        popper: { sx: { zIndex: POPPER_Z } },
        paper: {
          sx: {
            background: c.bg,
            color: c.textPrimary,
            border: `1px solid ${c.border}`,
            borderRadius: '10px',
            boxShadow: isDark ? '0 8px 28px rgba(0,0,0,0.55)' : '0 8px 28px rgba(30,67,150,0.16)',
            fontFamily: typography.fontFamily.body,
            '& .MuiAutocomplete-option': {
              fontSize, color: c.textPrimary, fontFamily: typography.fontFamily.body,
              '&[aria-selected="true"]': { background: `${c.textActive}14` },
              '&.Mui-focused': { background: c.bgHover },
            },
            '& .MuiAutocomplete-groupLabel': {
              fontSize: 10, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase',
              color: c.textMuted, background: c.bgSubtle, lineHeight: '28px',
            },
          },
        },
      }}
      renderOption={(props, option, { selected: isSel }) => {
        // key must be spread off props (MUI v6 puts key in props)
        const { key, ...rest } = props;
        return (
          <li key={key} {...rest} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {multiple && (
              <Checkbox
                size="small"
                checked={isSel}
                sx={{ p: 0, mr: 0.5, color: c.textMuted, '&.Mui-checked': { color: c.textActive } }}
              />
            )}
            {option.icon && (
              <span className="material-symbols-outlined" style={{ fontSize: 18, color: c.textMuted }}>
                {option.icon}
              </span>
            )}
            {option.label}
          </li>
        );
      }}
      renderTags={(tagValue, getTagProps) =>
        tagValue.map((option, index) => {
          const { key, ...tagProps } = getTagProps({ index });
          return (
            <Chip
              key={key}
              {...tagProps}
              label={option.label}
              size="small"
              sx={{
                height: 22, fontSize: 11, fontFamily: typography.fontFamily.body,
                background: `${c.textActive}14`, color: c.textActive,
                '& .MuiChip-deleteIcon': { color: c.textActive, fontSize: 15 },
              }}
            />
          );
        })
      }
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={multiple && selected.length ? undefined : placeholder}
          inputProps={{
            ...params.inputProps,
            'aria-label': ariaLabel ?? label ?? 'select',
            // Non-searchable: stop typing/filtering but keep the popup openable on
            // click (Autocomplete-level readOnly would disable the menu entirely).
            readOnly: !searchable,
          }}
          sx={{
            '& .MuiInputBase-root': {
              fontSize, fontFamily: typography.fontFamily.body, color: c.textPrimary,
              background: fieldBg, borderRadius: '8px',
              minHeight: fieldH, paddingTop: '0 !important', paddingBottom: '0 !important',
              cursor: searchable ? 'text' : 'pointer',
            },
            // Single-line input vertically centered within the fixed-height field.
            '& .MuiInputBase-input': {
              fontSize, padding: '0 4px', height: 'auto', cursor: searchable ? 'text' : 'pointer',
            },
            '& .MuiOutlinedInput-notchedOutline': { borderColor: c.border, borderWidth: '1px' },
            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: c.borderSubtle ?? c.border },
            '& .Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: c.textActive, borderWidth: '1px' },
            '& .MuiInputLabel-root': { fontSize: 11, color: c.textMuted, fontFamily: typography.fontFamily.body },
            '& .MuiSvgIcon-root': { color: c.textMuted },
            ...sx,
          }}
        />
      )}
    />
  );
}
